// 261010 UI1b — bảng shot Quay phim · nhãn đáy Storyboard · chồng bản Video · 9 cột (THUẦN, không antd).
// ⚠️ Tiêu cự (mm) SUY từ cỡ cảnh (bảng cố định theo mockup v7), CHƯA có trường thật trong dữ liệu — nhãn chỉ để người xem hình dung, không gửi engine.
import type { FilmEvent, FilmShot, StoryboardFrame } from '../../types/film';
import type { WorkflowEventStatus } from '../../types/filmWorkflow';
import { ANGLE_OPTIONS } from './storyboardUtils';
import { dialogueLine } from './workflowDetailUtils';

export interface ShotRow {
  key: string; no: number; sizeCode: string; sizeEn: string; angleLabel: string; mm: number | null;
  moveCode: string; moveEn: string; durationS: number | null; purpose: string; note: string; dialogue: string[];
}
export interface CaptionView { label: string; dialogue: string }
export interface ClipVersion { id: string; n: number; label: string; status: 'PENDING' | 'DONE' | 'FAILED' | 'UNKNOWN'; selected: boolean; durationS: number | null; url: string | null; error: string | null }

const MM: Record<string, number> = { EWS: 16, WS: 24, MWS: 28, MS: 35, MCU: 50, CU: 85, ECU: 100 };
const SIZE_EN: Record<string, string> = {
  EWS: 'extreme wide shot', WS: 'long shot', MWS: 'medium long shot', MS: 'medium shot', MCU: 'medium close-up', CU: 'close-up', ECU: 'extreme close-up',
};
const MOVE_EN: Record<string, string> = {
  STATIC: 'static', PUSH_IN: 'push in', PULL_OUT: 'dolly back', PAN: 'pan', TRACKING: 'tracking', ORBIT: 'orbit', HANDHELD: 'handheld', AERIAL: 'aerial',
  TILT_UP: 'tilt up', TILT_DOWN: 'tilt down', WHIP_PAN: 'whip pan', DOLLY_ZOOM: 'dolly zoom', CRANE_DOWN: 'crane down', PAN_360: '360 pan', SNAP_ZOOM: 'snap zoom',
};
const up = (s?: string | null): string => String(s ?? '').trim().toUpperCase();

export const focalMmOf = (size?: string | null): number | null => MM[up(size)] ?? null;
export const shotSizeEn = (size?: string | null): string => {
  const k = up(size);
  return !k ? 'shot' : SIZE_EN[k] ?? k.toLowerCase();
};
export const moveEn = (move?: string | null): string => {
  const k = up(move);
  return !k ? 'static' : MOVE_EN[k] ?? k.toLowerCase().replace(/_/g, ' ');
};
export const fmtSec = (n: number | null | undefined): string => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '?';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

/** Nhãn đáy một khung storyboard: `0s–5s | long shot | 24mm | dolly back` + thoại các shot nguồn (hoặc "No dialogue"). Thiếu phần nào bỏ phần đó. */
export function storyboardCaptionOf(
  f: Pick<StoryboardFrame, 'startS' | 'endS' | 'shotSize' | 'cameraMove' | 'sourceShotIds'>, shots?: FilmShot[] | null,
): CaptionView {
  const mm = focalMmOf(f.shotSize);
  const timed = Number.isFinite(f.startS) && Number.isFinite(f.endS);
  const parts = [timed ? `${fmtSec(f.startS)}s–${fmtSec(f.endS)}s` : '', shotSizeEn(f.shotSize), mm != null ? `${mm}mm` : '', moveEn(f.cameraMove)].filter(Boolean);
  const ids = new Set(f.sourceShotIds ?? []);
  const lines = (shots ?? []).filter((s) => ids.has(s.id)).flatMap((s) => (s.dialogueLines ?? []).map((l) => dialogueLine(l)).filter(Boolean));
  return { label: parts.join(' | '), dialogue: lines.length ? lines.join(' / ') : 'No dialogue' };
}

export function shotRowsOf(shots: FilmShot[] | null | undefined): ShotRow[] {
  return [...(shots ?? [])].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)).map((s) => ({
    key: s.id, no: s.orderIndex ?? 0, sizeCode: up(s.shotSize), sizeEn: s.shotSize ? shotSizeEn(s.shotSize) : '',
    angleLabel: s.angle ? (ANGLE_OPTIONS.find((o) => o.value === up(s.angle))?.label ?? String(s.angle)) : '',
    mm: focalMmOf(s.shotSize), moveCode: up(s.cameraMove), moveEn: s.cameraMove ? moveEn(s.cameraMove) : '',
    durationS: typeof s.durationS === 'number' ? s.durationS : null, purpose: String(s.purpose ?? '').trim(), note: String(s.frameNote ?? '').trim(),
    dialogue: (s.dialogueLines ?? []).map((l) => dialogueLine(l)).filter(Boolean),
  }));
}

const httpUrl = (u: unknown): string | null => (typeof u === 'string' && /^https?:\/\//i.test(u.trim()) ? u.trim() : null);
/** Chồng bản dọc: bản đang chọn lên đầu, rồi mới → cũ; hiện tối đa `max`, phần còn lại = "+N bản cũ". `n` = thứ tự tạo (1 = cũ nhất). */
export function clipVersionsOf(ev: Pick<FilmEvent, 'clips' | 'selectedClipId'>, max = 3): { versions: ClipVersion[]; hiddenCount: number } {
  const asc = [...(ev.clips ?? [])].sort((a, b) => String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? '')));
  const all: ClipVersion[] = asc.map((c, i) => ({
    id: c.id, n: i + 1, label: `Bản ${i + 1}`, status: c.status ?? 'UNKNOWN', selected: !!ev.selectedClipId && c.id === ev.selectedClipId,
    durationS: typeof c.durationS === 'number' ? c.durationS : null, url: httpUrl(c.videoUrl), error: c.error ? String(c.error) : null,
  }));
  const ordered = [...all.filter((v) => v.selected), ...all.filter((v) => !v.selected).reverse()];
  return { versions: ordered.slice(0, max), hiddenCount: Math.max(0, ordered.length - max) };
}

/**
 * Chọn bản ở chồng bản: video ĐÃ có phiếu AI chấm clip (`reviews.CLIP_REVIEW`) thì P6 có thể đòi hộp ghi lý do ⇒ KHÔNG chọn thẳng, mở ngăn Chi tiết (có hộp).
 * Chưa có phiếu ⇒ P6 không hỏi gì ⇒ `selectEventClip` thẳng, đúng hành vi hiện nay.
 */
export const clipSelectModeOf = (e: Pick<WorkflowEventStatus, 'reviews'>): 'DIRECT' | 'DRAWER' => (e.reviews?.CLIP_REVIEW ? 'DRAWER' : 'DIRECT');
